import { AttendanceStatus, AttendanceStatusMarshaller } from 'app/constants/enums/AttendanceStatus';
import {
  BookingCancellationType,
  BookingCancellationTypeMarshaller,
} from 'app/constants/enums/BookingCancellationType';
import { Language, LanguageMarshaller } from 'app/constants/enums/Language';
import { PaymentOverrideReason, PaymentOverrideReasonMarshaller } from 'app/constants/enums/PaymentOverrideReason';
import { PaymentStatus, PaymentStatusMarshaller } from 'app/constants/enums/PaymentStatus';
import { Category, ICategory } from 'app/constants/schemas/Category';
import { Course, ICourse } from 'app/constants/schemas/Course';
import { DateMarshaller } from 'app/constants/schemas/Date';
import { Duration, DurationMarshaller } from 'app/constants/schemas/Duration';
import { ILessonHeader, LessonHeader } from 'app/constants/schemas/LessonHeader';
import { ILessonStudent, LessonStudent } from 'app/constants/schemas/LessonStudent';
import { ILessonTeacher, LessonTeacher } from 'app/constants/schemas/LessonTeacher';
import { BoundedPeriod, BoundedPeriodMarshaller } from 'app/constants/schemas/Period';
import { IRoom, Room } from 'app/constants/schemas/rtc/Room';
import { IStaffProfile, StaffProfile } from 'app/constants/schemas/StaffProfile';
import vmClient from 'app/services/vmClient';
import { Entity, EntityStringId, IEntity, IEntityType } from 'app/types/entity';
import { ILessonBookingSummary, LessonBookingSummary } from './LessonBookingSummary';
import { CancellationReason, CancellationReasonMarshaller } from 'app/constants/enums/CancellationReason';

export interface ILessonBooking extends IEntity {
  attributes: {
    bonusPayBooking: boolean;
    cancellation: BookingCancellationType | null;
    cancellationMessage: string | null;
    cancellationReason: CancellationReason | null;
    cancellationTime: Date | null;
    createdAt: Date;
    id: EntityStringId;
    language: Language | null;
    occupiesStudent: boolean;
    occupiesTeacher: boolean;
    paymentOverride: PaymentStatus | null;
    paymentOverrideMessage: string | null;
    paymentOverrideReason: PaymentOverrideReason | null;
    serverDowntime: Duration | null;
    studentAttendance: AttendanceStatus | null;
    teacherAttendance: AttendanceStatus | null;
    teacherNoteSubmissionTime: Date | null;
    time: BoundedPeriod;
    endTime: Date | null;
    updatedAt: Date;
  };
  associations: {
    cancellationActor: IStaffProfile | null;
    category: ICategory | null;
    course: ICourse | null;
    lesson: ILessonHeader | null;
    paymentOverrider: IStaffProfile | null;
    rtcRoom: IRoom | null;
    student: ILessonStudent;
    summary: ILessonBookingSummary;
    teacher: ILessonTeacher;
  };
}

export const lessonBookingSchema = {
  typeName: 'LessonBooking',
  version: 10,
  lockable: true,
  persisted: true,

  attributes: {
    bonusPayBooking: {
      notNull: true,
      type: 'boolean',
    },
    cancellation: {
      type: BookingCancellationType,
      marshaller: BookingCancellationTypeMarshaller,
    },
    cancellationMessage: {
      type: 'string',
    },
    cancellationReason: {
      type: CancellationReason,
      marshaller: CancellationReasonMarshaller,
    },
    cancellationTime: {
      type: Date,
      marshaller: DateMarshaller,
    },
    createdAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
    language: {
      type: Language,
      marshaller: LanguageMarshaller,
    },
    occupiesStudent: {
      notNull: true,
      type: 'boolean',
    },
    occupiesTeacher: {
      notNull: true,
      type: 'boolean',
    },
    paymentOverride: {
      marshaller: PaymentStatusMarshaller,
      type: PaymentStatus,
    },
    paymentOverrideMessage: 'string',
    paymentOverrideReason: {
      marshaller: PaymentOverrideReasonMarshaller,
      type: PaymentOverrideReason,
    },
    serverDowntime: {
      marshaller: DurationMarshaller,
      type: Duration,
    },
    studentAttendance: {
      marshaller: AttendanceStatusMarshaller,
      type: AttendanceStatus,
    },
    teacherAttendance: {
      marshaller: AttendanceStatusMarshaller,
      type: AttendanceStatus,
    },
    teacherNoteSubmissionTime: {
      marshaller: DateMarshaller,
      type: Date,
    },
    time: {
      type: BoundedPeriod,
      notNull: true,
      marshaller: BoundedPeriodMarshaller,
    },
    endTime: {
      marshaller: DateMarshaller,
      type: Date,
    },
    updatedAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
  },

  associations: {
    cancellationActor: {
      shared: true,
      type: StaffProfile,
    },
    category: {
      type: Category,
      shared: true,
    },
    course: {
      type: Course,
      shared: true,
    },
    lesson: {
      type: LessonHeader,
      shared: true,
    },
    paymentOverrider: {
      shared: true,
      type: StaffProfile,
    },
    rtcRoom: {
      type: Room,
    },
    student: {
      type: LessonStudent,
      shared: true,
      notNull: true,
    },
    summary: {
      type: LessonBookingSummary,
    },
    teacher: {
      type: LessonTeacher,
      shared: true,
      notNull: true,
    },
  },
};

export type LessonBooking = Entity<ILessonBooking>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const LessonBooking: IEntityType<ILessonBooking> = vmClient.defineClass(lessonBookingSchema);
